import * as React from 'react';
import { Select, MenuItem, FormControl, InputLabel, IconButton, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { renderHelpText } from './renderHelpText';
import { useState } from 'react';
import HelpIcon from '@material-ui/icons/Help';

export interface ComboFieldOption {
    value: string;
    label?: React.ReactNode;
    disabled?: boolean;
    icon?: React.ReactNode;
}

export interface ComboFieldProps {
    value: string|undefined,
    options: (string | ComboFieldOption)[],
    id?: string,
    label?: string,
    labelIcon?: React.ReactNode,
    helpText?: string,
    disabled?: boolean,
    onChange: (value: string|undefined) => void,
}

export const ComboField = (props: ComboFieldProps) => {
    const {
        value,
        options,
        id,
        label,
        labelIcon,
        helpText,
        disabled,
        onChange,
    } = props;

    const labelId = label && id ? `${id}-label` : undefined;

    const [showHelp, setShowHelp] = useState(false);

    const getOptionConfig = (opt: string | ComboFieldOption) => ({
        value: typeof opt === 'string' ? opt : opt.value,
        label: typeof opt === 'string' ? opt : (opt.label ?? opt.value),
        disabled: typeof opt === 'string' ? false : opt.disabled,
        icon: typeof opt === 'string' ? undefined : opt.icon,
    });

    const renderOptionContent = (opt: string | ComboFieldOption) => {
        const option = getOptionConfig(opt);

        if (!option.icon) {
            return option.label;
        }

        return (
            <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)', minWidth: 0 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>{option.icon}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{option.label}</span>
            </span>
        );
    };

    const selectedOption = options.find(opt => getOptionConfig(opt).value === (value || ''));
    
    const renderInputLabel = () => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25em' }}>
            {labelIcon && <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: '1em', lineHeight: 1 }}>{labelIcon}</span>}
            {label}
            {helpText && (
                <IconButton size="small" onClick={(e: React.MouseEvent) => { e.stopPropagation(); e.preventDefault(); setShowHelp(true); }}>
                    <HelpIcon style={{ fontSize: '1rem' }} />
                </IconButton>
            )}
        </span>
    );

    return (
        <FormControl fullWidth size="small">
            {label &&
                <InputLabel id={labelId} shrink={true}>{renderInputLabel()}</InputLabel>
            }
            <Select
                id={id ? `${id}-input` : undefined}
                labelId={labelId}

                disabled={disabled}
                value={value || ''}
                displayEmpty

                label={label}
                renderValue={() => selectedOption ? (
                    <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {renderOptionContent(selectedOption)}
                    </span>
                ) : ''}

                onChange={e => onChange(e.target.value || undefined)}
                variant="outlined"
            >
                {
                    options.map(opt => {
                        const option = getOptionConfig(opt);
                        return (
                            <MenuItem
                                id={id ? `${id}-option-${option.value}` : undefined}
                                key={option.value}
                                value={option.value}
                                disabled={option.disabled}
                            >
                                {renderOptionContent(opt)}
                            </MenuItem>
                        );
                    })
                }
            </Select>
            {helpText?.length && (
                <Dialog open={showHelp} onClose={() => setShowHelp(false)}>
                    <DialogTitle>{labelIcon ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25em' }}>{labelIcon}{label}</span> : label}</DialogTitle>
                    <DialogContent>{renderHelpText(helpText)}</DialogContent>
                </Dialog>
            )}
        </FormControl>
    );
}
